import { memo, useMemo } from "react";
import { useEffect } from "react";
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import "./table.css";
import { TableLoader } from "../Loaders/tableLoader";
import { PaginationLoader } from "../Pagination/loader";
import { Pagination } from "../Pagination/index";
import { useNavigate } from "react-router-dom";
import GridCard from "../Cards/GridCard";

export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_TOTAL_PAGES = 1;
const DEFAULT_TOTAL_DATA = 0;
const TableComponent = ({
  data,
  columns,
  loadingData,
  onRowClick,
  setCurrentPage,
  currentPage,
  viewHandler,
  editHandler,
  deleteHandler,
  viewMode,
  isEditable,
}) => {
  const navigate = useNavigate();
  const memoizedData = useMemo(() => data ?? [], [data]);
  const memoizedColumns = useMemo(() => columns ?? [], [columns]);
  const tableInstance = useReactTable({
    columns: memoizedColumns,
    data: memoizedData,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const paginationData = useMemo(() => {
    return {
      pageSize: 12,
      totalPages: Math.ceil(data ? (data.length / 10) : 1),
      totalData: data ? data.length : 15,
    };
  }, [data]);

  const { totalPages, pageSize, totalData } = useMemo(() => {
    const itemsPerPage = paginationData?.pageSize || DEFAULT_PAGE_SIZE;
    const numOfPages = paginationData?.totalPages || DEFAULT_TOTAL_PAGES;
    const numOfData = paginationData?.totalData || DEFAULT_TOTAL_DATA;
    return {
      pageSize: itemsPerPage,
      totalPages: numOfPages,
      totalData: numOfData,
    };
  }, [paginationData]);

  // const { totalPages, pageSize, totalData } = paginationData;

  const visibleData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalData);
    return memoizedData.slice(startIndex, endIndex);
  }, [currentPage, pageSize, totalData, memoizedData]);


  return (
    <>
      {loadingData ? (
        <TableLoader />
      ) : (
        <>
          {viewMode === "grid" ? (

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-10 w-[100%]">
              {visibleData.map((cellData, i) => (
                <GridCard
                  key={i}
                  data={cellData}
                  viewHandler={viewHandler}
                  editHandler={editHandler}
                  deleteHandler={deleteHandler}
                  isEditable={isEditable}
                />
              ))}
            </div>
          ) : (
            <div className="custom-table-container overflow-x-auto w-full">
              <table className="custom-table">
                <thead>
                  {tableInstance.getHeaderGroups().map((headerEl) => {
                    return (
                      <tr key={headerEl.id}>
                        {headerEl.headers.map((columnEl) => {
                          return (
                            <th key={columnEl.id} colSpan={columnEl.colSpan}>
                              {columnEl.isPlaceholder
                                ? null
                                : flexRender(columnEl.column.columnDef.header, columnEl.getContext())}
                            </th>
                          );
                        })}
                      </tr>
                    );
                  })}
                </thead>
                <tbody>
                  {tableInstance.getRowModel().rows.map((rowEl) => {
                    return (
                      <tr key={rowEl.id} onClick={() => onRowClick(rowEl.original._id)} className="cursor-pointer hover:bg-[#f9f9f9]" >
                        {rowEl.getVisibleCells().map((cellEl) => {
                          return (
                            <td key={cellEl.id} className="truncate max-w-[250px]">
                              {flexRender(cellEl.column.columnDef.cell, cellEl.getContext())}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {loadingData ? (
        <PaginationLoader />
      ) : (
        currentPage &&
        setCurrentPage &&
        totalData > pageSize && (
          <Pagination
            totalRecords={totalData}
            currentPage={currentPage}
            totalPages={totalPages}
            onNextPage={() => {
              setCurrentPage((prev) => prev + 1);
              tableInstance.nextPage();
            }}
            onPrevPage={() => {
              setCurrentPage((prev) => prev - 1);
              tableInstance.previousPage();
            }}
            onPageChange={(page) => {
              setCurrentPage(page);
              tableInstance.setPageIndex(page - 1);
            }
            }
            pageSize={pageSize}
            paginationLoading={false}
          />
        )
      )}
    </>
  );
};

export const Table = memo(TableComponent);
